// Which providers take a `base_url`, in ONE place.
//
// The rule was stated twice — `<AddKeyModal/>`'s capture form and
// `<DonateKeyForm/>`, whose copy carried the comment "the same rule
// <AddKeyModal/> holds", which is a duplicate admitting it is one. Both forms
// gate a field on it and the capture probe gates a request on it, so a provider
// that gained an endpoint override in one and not the other would offer a field
// on one screen and refuse the credential on the other.

import { BYOKProvider, BYOKProviderSchema, vetProviderBaseUrl } from '@developerz/domain';

/**
 * REQUIRE the endpoint — exactly the providers the capture seam refuses without
 * one. Read off `vetProviderBaseUrl` (`@developerz/domain`), the function every
 * capture ingress and the verify probe ask, rather than restated here: `custom`
 * (an arbitrary OpenAI-compatible endpoint has no default) and `alibaba` (Model
 * Studio scopes its endpoint to the customer's workspace and region). A form that
 * asked for less would bounce off a 400; one that asked for more would refuse a
 * credential the server accepts. Whether a TYPED endpoint is recognized stays the
 * server's call — the refusal it answers names the shapes it admits.
 */
export const needsBaseUrl = (provider: string): boolean => {
  const parsed = BYOKProviderSchema.safeParse(provider);
  return parsed.success && !vetProviderBaseUrl(parsed.data, null).ok;
};

/**
 * Offer the endpoint field: wherever it is required, plus optional for `zai`
 * (the coding-plan path) and `kimi` (the Kimi Code path). Hidden for the rest,
 * which dial their own known host.
 */
export const wantsBaseUrl = (provider: string): boolean =>
  needsBaseUrl(provider) || provider === BYOKProvider.Zai || provider === BYOKProvider.Kimi;

/**
 * The copy key explaining where a provider's endpoint comes from, or null when
 * the field needs no explanation. Alibaba's does: the three endpoint shapes are
 * not guessable, and the workspace id they carry lives in the Model Studio
 * console, not on the key.
 */
export const baseUrlHintKey = (provider: string): string | null =>
  provider === BYOKProvider.Alibaba ? 'account_keys.base_url_hint_alibaba' : null;
